/**
 * NewsletterImagePicker — drop-in image control for a single newsletter item.
 *
 * Three sources (Spec — Dipali request 2026-04-29):
 *   1. Upload from device   (file picker → Cloudinary)
 *   2. AI Generate          (gpt-image-1 prompt from item title/description)
 *   3. Web Search           (Tavily candidate grid, pick to Cloudinary)
 *
 * Output: calls `onChange(cloudinaryUrl)` with the secure URL once anything
 * lands in Cloudinary.  Editor stores it in `item.image`.
 */
import React, { useState, useRef } from 'react';
import api from '@/lib/api';
import { Upload, Sparkles, Search, X, Image as ImageIcon, Loader2 } from 'lucide-react';

const MIN_QUERY_LEN = 2;

export default function NewsletterImagePicker({
  value,
  onChange,
  clientId,
  year,
  month,
  itemTitle = '',
  itemDescription = '',
  sectionTitle = '',
  testIdPrefix = 'np',
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(null);          // 'upload' | 'generate' | 'search' | 'import'
  const [error, setError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const setUrl = (u) => {
    setError(null);
    onChange?.(u);
  };

  const onUploadClick = () => fileRef.current?.click();

  const onFileSelected = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-upload of same file
    if (!f) return;
    setBusy('upload'); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('client_id', clientId);
      fd.append('year', String(year));
      fd.append('month', String(month));
      const r = await api.post('/newsletters/_image/upload', fd);
      setUrl(r.data.url);
    } catch (e2) {
      setError(e2?.response?.data?.detail || e2.message);
    } finally {
      setBusy(null);
    }
  };

  const onGenerate = async () => {
    if (!itemTitle && !itemDescription) {
      setError('Add a title or description first — that becomes the prompt.');
      return;
    }
    setBusy('generate'); setError(null);
    try {
      const r = await api.post('/newsletters/_image/generate', {
        client_id: clientId, year, month,
        title: itemTitle, description: itemDescription,
        section_title: sectionTitle,
      }, { timeout: 120000 });
      setUrl(r.data.url);
    } catch (e2) {
      setError(e2?.response?.data?.detail || e2.message);
    } finally {
      setBusy(null);
    }
  };

  const openSearch = () => {
    const seed = `${itemTitle} ${itemDescription}`.trim().slice(0, 120);
    setSearchQuery(seed);
    setSearchResults([]);
    setSearchOpen(true);
  };

  const runSearch = async () => {
    if (searchQuery.trim().length < MIN_QUERY_LEN) return;
    setBusy('search'); setError(null);
    try {
      const r = await api.post('/newsletters/_image/search', {
        query: searchQuery.trim(), max_results: 8,
      });
      setSearchResults(r.data.results || []);
    } catch (e2) {
      setError(e2?.response?.data?.detail || e2.message);
    } finally {
      setBusy(null);
    }
  };

  const pickWebImage = async (url) => {
    setBusy('import'); setError(null);
    try {
      const r = await api.post('/newsletters/_image/import', {
        client_id: clientId, year, month, url,
      });
      setUrl(r.data.url);
      setSearchOpen(false);
    } catch (e2) {
      setError(e2?.response?.data?.detail || e2.message);
    } finally {
      setBusy(null);
    }
  };

  const clearImage = () => onChange?.('');

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-image-picker`}>
      <div className="flex items-center gap-2 flex-wrap">
        <input ref={fileRef} type="file" accept="image/*"
          onChange={onFileSelected} className="hidden"
          data-testid={`${testIdPrefix}-image-file-input`} />
        <ImgBtn icon={<Upload size={11} />} label="Upload" onClick={onUploadClick}
          loading={busy === 'upload'} testId={`${testIdPrefix}-image-upload`} />
        <ImgBtn icon={<Sparkles size={11} />} label="AI Generate" onClick={onGenerate}
          loading={busy === 'generate'} accent="#7A5C2B" testId={`${testIdPrefix}-image-generate`} />
        <ImgBtn icon={<Search size={11} />} label="Web Search" onClick={openSearch}
          loading={busy === 'search'} testId={`${testIdPrefix}-image-search-open`} />
        {value && (
          <button onClick={clearImage}
            className="text-[11px] px-2 py-1 rounded border hover:bg-red-50 inline-flex items-center gap-1"
            style={{ borderColor: '#FCA5A5', color: '#A32D2D' }}
            data-testid={`${testIdPrefix}-image-clear`}>
            <X size={11} /> Remove
          </button>
        )}
      </div>

      {error && (
        <div className="text-[11px] px-2 py-1 rounded" style={{ background: '#FEE2E2', color: '#7a1f1f' }}>
          {error}
        </div>
      )}

      {value ? (
        <div className="flex items-start gap-2 p-2 border rounded-md" style={{ borderColor: '#E5E1DC' }}>
          <img src={value} alt="item" className="w-32 h-20 object-cover rounded"
            data-testid={`${testIdPrefix}-image-preview`} />
          <div className="text-[10px] break-all" style={{ color: '#6B7280' }}>{value}</div>
        </div>
      ) : (
        <div className="text-[10px] flex items-center gap-1 px-2 py-1 rounded" style={{ background: '#F7F5F2', color: '#9CA3AF' }}>
          <ImageIcon size={10} /> No image yet — pick one above. Without an image, the card shows a brand placeholder.
        </div>
      )}

      {searchOpen && (
        <div className="rounded-md border p-3 space-y-2" style={{ borderColor: '#E5E1DC', background: '#FFFCF6' }} data-testid={`${testIdPrefix}-image-search-panel`}>
          <div className="flex items-center gap-2">
            <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
              placeholder='e.g. "Bukhara restaurant ITC Maurya interior"'
              className="flex-1 px-2 py-1 border rounded text-[12px]"
              style={{ borderColor: '#E5E1DC' }}
              data-testid={`${testIdPrefix}-image-search-input`} />
            <button onClick={runSearch} disabled={busy === 'search'}
              className="px-3 py-1 rounded text-[11px] text-white inline-flex items-center gap-1"
              style={{ backgroundColor: '#1C304A' }}
              data-testid={`${testIdPrefix}-image-search-go`}>
              {busy === 'search' ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />}
              {busy === 'search' ? 'Searching…' : 'Search'}
            </button>
            <button onClick={() => setSearchOpen(false)}
              className="px-2 py-1 rounded text-[11px] border"
              style={{ borderColor: '#E5E1DC' }}
              data-testid={`${testIdPrefix}-image-search-close`}>
              Close
            </button>
          </div>

          {busy === 'import' && (
            <div className="text-[11px] inline-flex items-center gap-1" style={{ color: '#7A5C2B' }}>
              <Loader2 size={11} className="animate-spin" /> Saving to Cloudinary…
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {searchResults.map((r, idx) => (
                <button key={idx}
                  onClick={() => pickWebImage(r.url)}
                  disabled={busy === 'import'}
                  title={r.description || r.source_title || r.url}
                  className="group block rounded-md overflow-hidden border hover:shadow-md transition disabled:opacity-50"
                  style={{ borderColor: '#E5E1DC' }}
                  data-testid={`${testIdPrefix}-image-search-pick-${idx}`}>
                  <img src={r.url} alt={r.description || ''}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="w-full h-24 object-cover bg-gray-100" />
                  <div className="text-[10px] px-1.5 py-1 truncate text-left" style={{ color: '#6B7280' }}>
                    {r.source_title || (r.description || '').slice(0, 40) || 'Pick'}
                  </div>
                </button>
              ))}
            </div>
          )}

          {searchResults.length === 0 && busy !== 'search' && (
            <div className="text-[11px]" style={{ color: '#9CA3AF' }}>
              Type a query and press Search. Real-venue searches work best (e.g. "Le Calandre Padua dining room").
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ImgBtn({ icon, label, onClick, loading, accent, testId }) {
  return (
    <button onClick={onClick} disabled={loading} data-testid={testId}
      className="text-[11px] px-2 py-1 rounded border hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-60"
      style={{ borderColor: '#E5E1DC', color: accent || '#1B2A4A' }}>
      {loading ? <Loader2 size={11} className="animate-spin" /> : icon}
      {label}
    </button>
  );
}
